/**
 * The one notification entry point every part of the app uses (STEP
 * 11). Nothing calls `toast()` or a notification store directly outside
 * this file — one seam, one place to change behaviour.
 *
 * WHAT A NOTIFICATION NOW IS (desk, 2026-09-27, narrowed 2026-09-29). It used to
 * be a 4-second toast in the top-right corner: gone before a busy clerk could
 * read it, and for everything but errors, gone for good. `error` and `warning`
 * now show as a message in the CENTRE of the screen that stays until the user
 * clicks "Noted" (`NotificationDialog`), with a plain-language "what to do"
 * line; `success` and `info` are back to the corner toast, since a routine
 * confirmation click-through at a busy counter was the heavier failure mode.
 * Every severity is always kept in the bell as history regardless of which one
 * it renders as.
 *
 * `ACKNOWLEDGE` is the one switch: a severity set to `false` renders as the
 * corner toast instead of the centre dialog.
 */
import toast from 'react-hot-toast';
import { useNotificationStore } from '@store/notification.store';
import type { NotificationSeverity } from '@store/notification.store';
import { useNotificationAckStore } from '@store/notification-ack.store';
import { recentNextStepFor, recentWasOutage } from '@core/errors/api-error-handler';
import { outageVerdict } from '@core/api/backend-availability';

export interface NotifyOptions {
  title: string;
  message?: string;
  /** Keep a record in the bell (default: yes, for every severity — it is marked read once the user clicks Noted). */
  persist?: boolean;
  href?: string;
  /**
   * Leave the bell entry UNREAD even for a corner toast (`info`/`success`), so the bell's badge keeps counting it. Only for something that
   * is waiting for the person (a correction to verify or approve) and must not vanish with a 4-second toast. Default: marked read.
   */
  keepUnread?: boolean;
  /** What the person should do about it. Failures from the API get one automatically. */
  nextStep?: string;
}

/**
 * Which severities must be acknowledged with "Noted" — and therefore render
 * in the CENTRE dialog. `false` = the old 4-second corner toast.
 *
 * Desk, 2026-09-29: "only error and warning should show center... info,
 * success and system update not required in center." Narrowed from "all
 * four" (2026-09-27's original instruction was "any notification") once a
 * centred, click-through dialog for every routine "Saved" proved too heavy
 * at a busy counter — exactly the failure mode this file's own header
 * already anticipated ("if a routine success proves too heavy... flip
 * `success` here"). `error` and `warning` stay centred because those are the
 * two severities where missing the message has a cost worth interrupting
 * the counter for; `success`/`info` are confirmations, not interruptions.
 */
export const ACKNOWLEDGE: Record<NotificationSeverity, boolean> = {
  success: false,
  error: true,
  warning: true,
  info: false,
};

function toastMessage({ title, message }: NotifyOptions): string {
  return message ? `${title} — ${message}` : title;
}

function deliver(severity: NotificationSeverity, options: NotifyOptions): void {
  const bellId =
    options.persist ?? true
      ? useNotificationStore.getState().add({ title: options.title, message: options.message, severity, href: options.href })
      : undefined;

  if (!ACKNOWLEDGE[severity]) {
    // The bell entry is the record; nobody is asked to acknowledge a corner toast.
    if (bellId && !options.keepUnread) useNotificationStore.getState().markRead(bellId);
    if (severity === 'success') toast.success(toastMessage(options));
    else if (severity === 'error') toast.error(toastMessage(options));
    else toast(toastMessage(options), { icon: severity === 'warning' ? '⚠️' : 'ℹ️' });
    return;
  }

  const wantsAdvice = severity === 'error' || severity === 'warning';
  // Read NOW: the record it comes from only lives a few seconds, and an outage verdict can take longer.
  const nextStep = options.nextStep ?? (wantsAdvice ? recentNextStepFor(options.message) : undefined);
  const enqueue = (): void =>
    useNotificationAckStore.getState().enqueue({ severity, title: options.title, message: options.message, nextStep, bellId });

  /*
    A FAILURE CAUSED BY THE SERVER BEING AWAY WAITS FOR THE VERDICT.
    During a deploy every click fails; a "This did not go through" for each of
    them, on top of the system-update notice, is the noise that made users think
    the application was bad. If the server is confirmed down the update notice
    already says everything, so the message is folded into it (still kept in the
    bell, marked read). If it turns out to be a one-off blip, it is shown as usual.
  */
  const verdict = wantsAdvice && recentWasOutage(options.message) ? outageVerdict() : null;
  if (verdict) {
    void verdict.then((result) => {
      if (result === 'blip') enqueue();
      else if (bellId) useNotificationStore.getState().markRead(bellId);
    });
    return;
  }
  enqueue();
}

export const notify = {
  success(options: NotifyOptions): void {
    deliver('success', options);
  },
  error(options: NotifyOptions): void {
    deliver('error', options);
  },
  warning(options: NotifyOptions): void {
    deliver('warning', options);
  },
  info(options: NotifyOptions): void {
    deliver('info', options);
  },
};

/** Convenience for `core/errors/api-error-handler.ts`'s own `UserFacingError` shape — every catch block that wants a notification can go straight from a caught error to one call. */
export function notifyApiError(userFacingError: { title: string; message: string; severity: 'error' | 'warning' | 'info'; nextStep?: string }): void {
  notify[userFacingError.severity]({ title: userFacingError.title, message: userFacingError.message, nextStep: userFacingError.nextStep });
}
